import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { authApi, setCsrfToken } from "../api/client";
import type { AuthState } from "../types";
import { AuthContext } from "./authContext";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ configured: true, authenticated: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearSession = useCallback(() => {
    setCsrfToken(undefined);
    setState((current) => ({ configured: current.configured, authenticated: false }));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    authApi.state(controller.signal).then(
      (next) => {
        setState(next);
        setCsrfToken(next.csrfToken);
        setError(null);
        setLoading(false);
      },
      (reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(reason instanceof Error ? reason.message : "Authentication service unavailable");
          setLoading(false);
        }
      },
    );
    const unauthorized = () => {
      clearSession();
    };
    window.addEventListener("auto-pools:unauthorized", unauthorized);
    return () => {
      controller.abort();
      window.removeEventListener("auto-pools:unauthorized", unauthorized);
    };
  }, [clearSession]);

  const login = useCallback(async (username: string, password: string) => {
    const result = await authApi.login(username, password);
    setCsrfToken(result.csrfToken);
    setState({
      configured: true,
      authenticated: true,
      csrfToken: result.csrfToken,
      role: "operator",
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo(
    () => ({ ...state, viewer: state.role === "viewer", loading, error, login, logout }),
    [state, loading, error, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
